// Import package components
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Image, Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import classnames from "classnames";
import moment from "moment";
import UIkit from "uikit";

import { Link } from "../../ui";
import { AppContexts } from "../../providers";

// Import common components
import RecordsPerPage from "../common/RecordsPerPage";
import Pagination from "../common/Pagination";
import ChatbotFaqsAdd from "../chatbot_faqs/add";
import Dialog from "../common/Dialog";
import AddToFaqModal from "./add";

const ChatbotUnansweredQuestions = () => {
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;
  const { 
    loading, 
    unansweredQuestions, 
    listUnansweredQuestions, 
    deleteUnansweredQuestion,
    setLoading,
  } = useContext(AppContexts.ChatbotContext);
  const [total, setTotal] = useState(0);
  const [criteria, setCriteria] = useState({
    search_term: "",
    filter: "unresolved",
    page: 0
  });
  const [deleteId, setDeleteId] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState([]);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listUnansweredQuestions(
        criteria.filter,
        criteria.search_term,
        criteria.page,
        recordsPerPage
      );
      if (response) {
        setTotal(response.total);
      }
      setLoading(false);
    } catch (error) {
      UIkit.notification({
        message: "Failed to load unanswered questions",
        status: "danger",
      });
      setLoading(false);
    }
  }, [criteria.filter, criteria.search_term, criteria.page, recordsPerPage, listUnansweredQuestions]);

  useEffect(() => {
    document.title = "AI Chatbot Unanswered Questions - RueBaRue";
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [criteria.filter, criteria.search_term, criteria.page, recordsPerPage]);

  useEffect(() => {
    // keep selected flags in sync with the loaded questions
    if ((unansweredQuestions || []).length !== selectedFlags.length) {
      setSelectedFlags((unansweredQuestions || []).map(() => false));
    }
  }, [(unansweredQuestions || []).length]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      await deleteUnansweredQuestion(id);
      UIkit.notification({
        message: "Question deleted successfully",
        status: "success",
      });
      await loadQuestions();
    } catch (error) {
      UIkit.notification({
        message: "Failed to delete question",
        status: "danger",
      });
    }
  }, [deleteUnansweredQuestion, loadQuestions]);

  const openDeleteModal = (id) => {
    setDeleteId(id);
    UIkit.modal('#unanswered-question-delete-modal').show();
  };
  const closeDeleteModal = () => {
    setDeleteId(null);
    UIkit.modal('#unanswered-question-delete-modal').hide();
  };

  const { selectedAll, selectedAny } = useMemo(() => {
    const hasFlags = selectedFlags.length > 0;
    const all = hasFlags && selectedFlags.every((checked) => checked);
    const any = hasFlags && selectedFlags.some((checked) => checked);
    return { selectedAll: all, selectedAny: any };
  }, [selectedFlags]);

  const handleSelect = useCallback((checked, index) => {
    setSelectedFlags((flags) => [
      ...flags.slice(0, index),
      checked,
      ...flags.slice(index + 1),
    ]);
  }, []);

  const handleSelectAll = useCallback((event) => {
    setSelectedFlags((flags) => flags.map(() => event.target.checked));
  }, []);

  const openBulkDeleteModal = () => {
    UIkit.modal('#unanswered-questions-bulk-delete-modal').show();
  };

  const closeBulkDeleteModal = () => {
    UIkit.modal('#unanswered-questions-bulk-delete-modal').hide();
  };

  const handleDeleteSelected = useCallback(async () => {
    const ids = selectedFlags
      .map((flag, idx) => (flag ? unansweredQuestions[idx]?.id : null))
      .filter((id) => id);

    if (ids.length === 0) {
      UIkit.notification({ message: 'No questions selected', status: 'warning' });
      closeBulkDeleteModal();
      return;
    }

    try {
      setLoading(true);
      // delete all selected questions in parallel
      await Promise.all(ids.map((id) => deleteUnansweredQuestion(id)));
      UIkit.notification({ message: 'Selected questions deleted', status: 'success' });
      // refresh list
      await loadQuestions();
      // clear selections
      setSelectedFlags((unansweredQuestions || []).map(() => false));
      setLoading(false);
      closeBulkDeleteModal();
    } catch (error) {
      setLoading(false);
      UIkit.notification({ message: 'Failed to delete selected questions', status: 'danger' });
    }
  }, [selectedFlags, unansweredQuestions, deleteUnansweredQuestion, loadQuestions]);

  return (
    <>
      <section id="schedule-a-message">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">AI Chatbot Unanswered Questions</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form
                    action=""
                    className="uk-form uk-flex uk-flex-middle hms-form"
                  >
                    <div className="uk-inline">
                      <div className="uk-search uk-search-default uk-form-custom">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Question"
                          value={criteria.search_term}
                          onChange={(event) =>
                            handleCriteriaChange(
                              "search_term",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="uk-inline">
                      <div uk-form-custom="target: > * > span:first-child">
                        <select
                          value={criteria.filter}
                          onChange={(e) =>
                            handleCriteriaChange("filter", e.target.value)
                          }
                        >
                          <option value="unresolved">Unresolved</option>
                          <option value="resolved">Resolved</option>
                          <option value="all">All</option>
                        </select>
                        <button
                          className="uk-button uk-button-default"
                          type="button"
                          tabIndex="-1"
                        >
                          <span></span>
                          <span uk-icon="icon: chevron-down"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="hms-guest-btn">
                  <button
                    className="uk-button hms-btn hms-gray-btn"
                    onClick={openBulkDeleteModal}
                    disabled={!selectedAny}
                  >
                    <span uk-icon="icon: trash"></span> Delete
                  </button>
                </div>
              </Flex>
              
              <table className="uk-table uk-table-hover uk-table-divider hms-table faq-table">
                <thead>
                  <tr>
                    <th className="uk-table-shrink">
                      <label htmlFor="checkboxQAAll" className="checkboxContact">
                        <input type="checkbox" id="checkboxQAAll" checked={selectedAll} onChange={handleSelectAll} />
                      </label>
                    </th>
                    <th className="uk-table-expand">Unanswered Question</th>
                    <th className="uk-table-shrink">Asked At</th>
                    <th className="uk-table-shrink">Status</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="uk-text-center">
                        <div uk-spinner=""></div>
                        Loading...
                      </td>
                    </tr>
                  ) : unansweredQuestions.length > 0 ? (
                    unansweredQuestions.map((question, idx) => (
                      <tr key={question.id}>
                        <td>
                          <div className="autosend-checkbox">
                            <label htmlFor={`qaCheckbox${idx}`}>
                              <input
                                type="checkbox"
                                id={`qaCheckbox${idx}`}
                                checked={selectedFlags[idx]}
                                onChange={(event) => handleSelect(event.target.checked, idx)}
                              />
                            </label>
                          </div>
                        </td>
                        <td>{question.question}</td>
                        <td>{moment(question.created_at).format("MM/DD/YYYY hh:mm A")}</td>
                        <td>
                          <span className={classnames("uk-label", {
                            "uk-label-success": question.resolved,
                            "uk-label-warning": !question.resolved
                          })}>
                            {question.resolved ? "Resolved" : "Unresolved"}
                          </span>
                        </td>
                        <td className="hms-action-btn">
                          <div className="uk-flex uk-flex-top">
                            {!question.resolved && (
                              <div className="uk-inline">
                                <button
                                  className="uk-button"
                                  uk-tooltip="Add to FAQ"
                                  uk-toggle="target: #add-unanswered-to-faq-modal"
                                  onClick={() => {
                                    window.editingQuestion = question;
                                  }}
                                >
                                  <Image src="/images/add.svg" />
                                </button>
                              </div>
                            )}
                            <div className="uk-inline">
                              <button
                                className="uk-button hms-btn hms-white-btn"
                                uk-toggle="target: #unanswered-question-delete-modal"
                                type="button"
                                uk-tooltip="Delete"
                                onClick={() => openDeleteModal(question.id)}
                              >
                                <Image src="/images/delete.svg" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ padding: "30px 25px" }}>
                        No unanswered questions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={total}
              index={criteria.page}
              onIndexChanged={(page) => handleCriteriaChange("page", page)}
            />
            {total > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>
      <AddToFaqModal onSuccess={loadQuestions} />
      {/* Bulk Delete Confirmation Modal */}
      <Dialog id="unanswered-questions-bulk-delete-modal">
        <div className="uk-modal-dialog uk-modal-body">
          <button className="uk-modal-close-default" type="button" uk-close="true" onClick={closeBulkDeleteModal}></button>
          <h2 className="uk-modal-title">Delete Selected Questions</h2>
          <p>Are you sure you want to delete the selected questions?</p>
          <p className="uk-text-right">
            <button className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel" type="button" onClick={closeBulkDeleteModal}>Cancel</button>
            <button className="uk-button hms-btn hms-gray-btn" type="button" onClick={async () => { await handleDeleteSelected(); }} disabled={loading}>
              {loading ? <div data-uk-spinner="ratio: 0.5;"></div> : "Delete"}
            </button>
          </p>
        </div>
      </Dialog>
      {/* Delete Confirmation Modal */}
      <Dialog id="unanswered-question-delete-modal">
        <div className="uk-modal-dialog uk-modal-body">
          <button className="uk-modal-close-default" type="button" uk-close="true" onClick={closeDeleteModal}></button>
          <h2 className="uk-modal-title">Delete Question</h2>
          <p>Are you sure you want to delete?</p>
          <p className="uk-text-right">
            <button className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel" type="button" onClick={closeDeleteModal}>Cancel</button>
            <button className="uk-button hms-btn hms-gray-btn" type="button" onClick={async () => { await handleDelete(deleteId); closeDeleteModal(); }} disabled={loading}>
              {loading ? <div data-uk-spinner="ratio: 0.5;"></div> : "Delete"}
            </button>
          </p>
        </div>
      </Dialog>
    </>
  );
};

export default ChatbotUnansweredQuestions;
